<template>
  <FormContainer class="verify-email__setup-password-form__form-container">
    <div class="setup-password-form__text-block">
      <h2 class="setup-password-form__title">Вітаю в UConnect</h2>
      <p class="setup-password-form__description">Ми надійна CRM-платформа для агентів і партнерських мереж</p>
    </div>
    <StatusProgress
      class="setup-password-form__progress"
      current-step="setup-password"
    />
    <p class="setup-password-form__intro">Ваш e-mail підтверджено!</p>
    <p class="setup-password-form__intro">Задайте власний пароль, щоб завершити реєстрацію та мати змогу входити до акаунту.</p>
    <form class="setup-password__form" @submit.prevent="handleSubmit">
      <LabeledTextInput
        class="setup-password__input--password"
        label="Пароль"
        placeholder="*********"
        type="password"
        v-model="form.password"
        :error-messages="errors.password ? [errors.password] : []"
        @input="clearError('password')"
      />
      <p class="setup-password__input--description">Пароль має містити не менше 8 символів.</p>
      <LabeledTextInput
        class="setup-password__input--confirm-password"
        label="Повторіть пароль"
        placeholder="*********"
        type="password"
        v-model="form.confirmPassword"
        :error-messages="errors.password ? [errors.password] : []"
        @input="clearError('confirmPassword')"
      />
      <div class="setup-password__register">
        <OrangeFancyButton
          class="setup-password__register--button"
          type="submit"
          :disabled="(!form.password && !form.confirmPassword) || isSetupPasswordLoading"
        >
          <SpinnerLoader
            v-show="isSetupPasswordLoading"
            class="setup-password__controls--loader"
            color="#FFFFFF"
          />
          Зареєструватися
        </OrangeFancyButton>
      </div>
      <div class="setup-password__login">
        <p class="setup-password__login-text">
          У вас вже є аккаунт?
          <SmartLink
            class="setup-password__link--login"
            :route="{ name: 'login' }"
          >
            Увійти
          </SmartLink>
        </p>
      </div>
    </form>
  </FormContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import StatusProgress from '../../status-progress';
import { MetaTrackerEvents } from '@/shared/config/tracker-events';
import { metaTracker } from '@/shared/lib/tracker';
import { FormContainer } from '@/shared/ui/account';
import { Auth } from '@/shared/lib/auth';
import { LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { SpinnerLoader } from '@/shared/ui/loader';

export default {
  props: {
    email: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: {
        password: '',
        confirmPassword: '',
      },
      errors: {
        password: '',
      },
    };
  },
  mixins: [ metaTracker ],
  components: { SpinnerLoader, StatusProgress, FormContainer, SmartLink, LabeledTextInput, OrangeFancyButton },
  computed: {
    ...mapGetters('registerModule', [
      'isEmailVerificationReady', 'getEmailVerificationStatus', 'isSetupPasswordLoading', 'getSetupPasswordError',
    ]),
  },
  methods: {
    ...mapActions('registerModule', ['setupPassword']),
    clearError(field) {
      this.errors[field] = '';
    },
    validateForm() {
      return this.validatePassword() && this.validatePasswordLength();
    },
    validatePassword() {
      if (this.form.password.trim() !== this.form.confirmPassword.trim()) {
        this.errors.password = 'Паролі не співпадають';

        return false;
      }

      return true;
    },
    validatePasswordLength() {
      if (this.form.password.length < 8) {
        this.errors.password = 'Пароль має бути не менше 8 символів';

        return false;
      }

      return true;
    },
    async handleSubmit() {
      if (this.validateForm()) {
        try {
          const authData = await this.setupPassword({ email: this.email, token: this.token, password: this.form.password });

          if (authData && authData.token) {
            const token = authData.token;
            const isPartner = authData.isPartner ? 1 : 0;
            const isShowBonus = authData.isShowBonus ? 1 : 0;
            const info = authData.info;

            Auth.persistSession({ token, keyUser: 'user_' + info.id, info, isPartner, isShowBonus });

            if (window.axios) {
              window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
            }

            this.$store.commit('AUTH_SUCCESS', {
              token: token,
              isPartner: isPartner,
              isShowBonus: isShowBonus,
              info: info
            });
            this.$router.push({ name: 'prime' });
          } else {
            this.$router.push({ name: 'login' });
          }
        } catch (err) {
          this.errors.password = this.getSetupPasswordError;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.verify-email__setup-password-form {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }
}

.setup-password-form {
  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }

  &__progress {
    margin-bottom: 16px;
  }

  &__intro {
    font-size: 18px;
    font-weight: 400;
    color: #494E53;

    @media (max-width: 630px) {
      font-size: 16px;
    }
  }
}

.setup-password {
  &__input {
    &--confirm-password {
      margin-bottom: 32px;
    }
  }

  &__register {
    margin-bottom: 32px;
  }

  &__login {
    text-align: center;
  }

  &__link {
    &--login {
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #6171E4;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__controls {
    &--loader {
      margin-right: 12px;
    }
  }
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text, .input-text, ::v-deep .select, .select {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .orange-fancy-button__content {
  display: flex;
  justify-content: center;
}

::v-deep .setup-password__input--confirm-password .input-text--error {
  display: none !important;
}
</style>
